import { Head, usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Alert,
  Divider,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  TypographyStylesProvider,
} from '@mantine/core'
import { Plus, Search, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Fragment, useCallback, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import AppLayout from '@/components/AppLayout'
import ChatInputBox from '@/components/ChatInputBox'
import SidebarLayout from '@/components/SidebarLayout'
import type { ChatInputBoxHandle } from '@/components/ChatInputBox'
import ChatListItem from '@/components/ChatListItem'
import { useVoiceRecorder } from '@/lib/useVoiceRecorder'
import MessageBubble from '@/components/MessageBubble'
import TodoListWidget from '@/components/TodoListWidget'
import ToolCallGroup from '@/components/ToolCallGroup'
import { useChatSessions } from '@/lib/useChatSessions'
import { useChatMessages } from '@/lib/useChatMessages'
import type { AgentSessionProps, AssistantSegment, ChatEntry, ProjectChatsPageProps, SearchResultSession } from '@/types'
import { Group } from '@mantine/core'

function ProjectChatsIndex() {
  const {
    project,
    sessions: initialSessions,
    current_session,
    current_session_id,
    messages: initialMessages,
    todos: initialTodos,
    default_agent,
    csrf_token,
    available_models,
  } = usePage<ProjectChatsPageProps>().props

  const {
    sessions,
    setSessions,
    activeSessionId,
    setActiveSessionId,
    activeSession,
    setActiveSession,
    searchQuery,
    searchLoading,
    displayedSessions,
    handleSearch,
    updateSessionInList,
  } = useChatSessions(initialSessions, current_session_id, current_session, project.id)

  const {
    entries,
    setEntries,
    todos,
    setTodos,
    streaming,
    setStreaming,
    messagesEndRef,
  } = useChatMessages(
    initialMessages,
    initialTodos,
    current_session ? ['queued', 'running'].includes(current_session.status) : false,
    activeSessionId,
    activeSession,
    updateSessionInList,
    setActiveSession,
  )

  const inputBoxRef = useRef<ChatInputBoxHandle>(null)
  const [selectedModel, setSelectedModel] = useState(current_session?.model_id || 'fun:auto')
  const [flashNotice, setFlashNotice] = useState<string | null>(null)

  const { state: voiceState, levelHistory, startRecording, stopRecording } = useVoiceRecorder({
    csrfToken: csrf_token || '',
    onTranscription: (text) => inputBoxRef.current?.appendText(text),
  })

  const switchToSession = useCallback(async (sessionId: string) => {
    if (sessionId === activeSessionId) return

    setStreaming(false)

    try {
      const response = await fetch(`/sessions/${sessionId}/runtime`, {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return

      const payload = await response.json() as { session: AgentSessionProps; messages: ChatEntry[]; todos: typeof todos }
      setActiveSessionId(sessionId)
      setActiveSession(() => payload.session)
      setEntries(payload.messages)
      setTodos(payload.todos)
      setStreaming(['queued', 'running'].includes(payload.session.status))
      setSelectedModel(payload.session.model_id || 'fun:auto')
      window.history.replaceState({}, '', `/projects/${project.id}/chats?session=${sessionId}`)
    } catch {
      // ignore network errors on switch
    }
  }, [activeSessionId, project.id, setStreaming, setActiveSessionId, setActiveSession, setEntries, setTodos])

  const resetToNewChat = useCallback(() => {
    setStreaming(false)
    setActiveSessionId(null)
    setActiveSession(() => null)
    setEntries([])
    setTodos([])
    inputBoxRef.current?.clear()
    setSelectedModel('fun:auto')
    window.history.replaceState({}, '', `/projects/${project.id}/chats`)
  }, [project.id, setStreaming, setActiveSessionId, setActiveSession, setEntries, setTodos])

  const startResume = useCallback(async (body: Record<string, unknown>) => {
    if (!activeSessionId || !activeSession) return

    const response = await fetch(`/sessions/${activeSessionId}/resume`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf_token || '',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) throw new Error('Failed to resume.')

    const payload = await response.json() as { session: AgentSessionProps; messages: ChatEntry[] }
    setEntries(payload.messages)
    updateSessionInList(payload.session)

    setEntries((prev) => [...prev, { role: 'assistant', segments: [], created_at: new Date().toISOString() }])
    setStreaming(true)
  }, [activeSession, activeSessionId, csrf_token, updateSessionInList, setEntries, setStreaming])

  const canLaunchBackground = !activeSessionId || (activeSession?.message_count === 0)

  const handleSubmit = async (text: string, options?: { headless?: boolean }) => {
    if (!text || streaming) return

    const isHeadless = options?.headless === true

    if (activeSessionId && activeSession) {
      const userEntry: ChatEntry = { role: 'user', content: text, created_at: new Date().toISOString() }
      const assistantEntry: ChatEntry = { role: 'assistant', segments: [], created_at: new Date().toISOString() }

      if (!isHeadless) {
        setEntries((prev) => [...prev, userEntry, assistantEntry])
        setStreaming(true)
      }

      try {
        const response = await fetch(`/chats`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrf_token || '',
          },
          body: JSON.stringify({
            message: text,
            agent_session_id: activeSessionId,
            model_id: selectedModel,
            ...(isHeadless ? { headless: true } : {}),
          }),
        })

        if (!response.ok) {
          if (!isHeadless) setStreaming(false)
          return
        }

        const payload = await response.json() as { session: AgentSessionProps }
        updateSessionInList(payload.session)

        if (isHeadless) {
          setFlashNotice('Chat is now running in the background')
          resetToNewChat()
          setTimeout(() => setFlashNotice(null), 5000)
        }
      } catch {
        if (!isHeadless) setStreaming(false)
      }
      return
    }

    if (!default_agent) return

    try {
      const createResponse = await fetch(`/agents/${default_agent.id}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrf_token || '',
        },
        body: JSON.stringify({
          project_id: project.id,
          interactive: !isHeadless,
          model_id: selectedModel,
        }),
      })

      if (!createResponse.ok) return

      const createPayload = await createResponse.json() as { session: AgentSessionProps; messages: ChatEntry[] }
      const newSession = createPayload.session
      const newSessionId = newSession.id

      if (!isHeadless) {
        setActiveSessionId(newSessionId)
        setActiveSession(() => newSession)
        setSessions((prev) => [newSession, ...prev])
        window.history.replaceState({}, '', `/projects/${project.id}/chats?session=${newSessionId}`)
      } else {
        setSessions((prev) => [newSession, ...prev])
      }

      const userEntry: ChatEntry = { role: 'user', content: text, created_at: new Date().toISOString() }
      const assistantEntry: ChatEntry = { role: 'assistant', segments: [], created_at: new Date().toISOString() }

      if (!isHeadless) {
        setEntries([userEntry, assistantEntry])
        setStreaming(true)
      }

      const chatResponse = await fetch(`/chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf_token || '',
        },
        body: JSON.stringify({
          message: text,
          agent_session_id: newSessionId,
          model_id: selectedModel,
          ...(isHeadless ? { headless: true } : {}),
        }),
      })

      if (!chatResponse.ok) {
        if (!isHeadless) setStreaming(false)
        return
      }

      const chatPayload = await chatResponse.json() as { session: AgentSessionProps }
      updateSessionInList(chatPayload.session)

      if (isHeadless) {
        setFlashNotice('Chat is now running in the background')
        resetToNewChat()
        setTimeout(() => setFlashNotice(null), 5000)
      }
    } catch {
      if (!isHeadless) setStreaming(false)
    }
  }

  const renderAssistantSegments = (segments: AssistantSegment[], entryIndex: number, isLastEntry: boolean) => {
    if (segments.length === 0 && streaming && isLastEntry) {
      return <MessageBubble><Text size="sm">Thinking...</Text></MessageBubble>
    }

    return segments.map((segment, segmentIndex) => {
      if (segment.type === 'tool_calls') {
        return (
          <ToolCallGroup
            key={`${entryIndex}-tc-${segmentIndex}`}
            calls={segment.calls}
            onResume={startResume}
          />
        )
      }

      if (!segment.content) return null

      return (
        <MessageBubble key={`${entryIndex}-text-${segmentIndex}`}>
          <TypographyStylesProvider>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{segment.content}</ReactMarkdown>
          </TypographyStylesProvider>
        </MessageBubble>
      )
    })
  }

  const inputBox = (
    <ChatInputBox
      ref={inputBoxRef}
      onSubmit={handleSubmit}
      disabled={streaming || voiceState === 'recording'}
      loading={streaming}
      availableModels={available_models}
      selectedModel={selectedModel}
      onModelChange={setSelectedModel}
      voiceState={voiceState}
      levelHistory={levelHistory}
      onVoiceToggle={() => voiceState === 'recording' ? stopRecording() : startRecording()}
      ready={!!default_agent && !streaming}
      backgroundEligible={canLaunchBackground}
      autoFocus
    />
  )

  return (
    <>
      <Head title={`Chats - ${project.name}`} />

      <SidebarLayout sidebarWidth={280} sidebar={({ closeSidebar }) => (
        <>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Group justify="space-between" align="center">
              <Text fw={600} size="sm" lineClamp={1}>{project.name}</Text>
              <ActionIcon variant="light" size="sm" onClick={() => { resetToNewChat(); closeSidebar(); }} title="New chat">
                <Plus size={14} />
              </ActionIcon>
            </Group>
          </div>

          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <TextInput
              placeholder="Search chats..."
              leftSection={<Search size={14} />}
              size="xs"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              rightSection={
                searchLoading ? <Loader size={14} /> :
                searchQuery ? (
                  <ActionIcon size="xs" variant="subtle" onClick={() => handleSearch('')}>
                    <X size={12} />
                  </ActionIcon>
                ) : null
              }
            />
          </div>

          <ScrollArea style={{ flex: 1 }}>
            {displayedSessions.length === 0 && searchQuery ? (
              <Text c="dimmed" size="sm" ta="center" py="md">No matching chats found.</Text>
            ) : (
              displayedSessions.map((session, index) => (
                <Fragment key={session.id}>
                  {index > 0 && <Divider />}
                  <ChatListItem
                    session={session}
                    active={session.id === activeSessionId}
                    onClick={() => { switchToSession(session.id); closeSidebar(); }}
                    matchPreview={'match_preview' in session ? (session as SearchResultSession).match_preview : null}
                  />
                </Fragment>
              ))
            )}
          </ScrollArea>
        </>
      )}>
          {flashNotice && (
            <Alert
              color="teal"
              variant="light"
              withCloseButton
              onClose={() => setFlashNotice(null)}
              mx="md"
              mt="md"
            >
              {flashNotice}
            </Alert>
          )}

          {!activeSessionId ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: 600, padding: '0 24px' }}>
                <Text ta="center" c="dimmed" mb="lg">Start a new conversation</Text>
                {inputBox}
              </div>
            </div>
          ) : (
            <>
              {todos.length > 0 && <TodoListWidget todos={todos} />}
              <ScrollArea style={{ flex: 1 }} p="md">
                <Stack gap="md" pr="sm">
                  {entries.length === 0 ? (
                    <Text c="dimmed" ta="center" py="xl">Send a message to start chatting.</Text>
                  ) : (
                    entries.map((entry, index) => {
                      if (entry.role === 'user') {
                        return (
                          <MessageBubble key={index} align="right" tone="user">
                            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                              {entry.content}
                            </Text>
                          </MessageBubble>
                        )
                      }

                      return (
                        <Stack key={index} gap="sm">
                          {renderAssistantSegments(entry.segments, index, index === entries.length - 1)}
                        </Stack>
                      )
                    })
                  )}
                  <div ref={messagesEndRef} />
                </Stack>
              </ScrollArea>

              <div style={{ borderTop: '1px solid var(--mantine-color-gray-3)', padding: '12px 16px' }}>
                <Stack gap="sm">
                  {activeSession?.waiting_reason ? (
                    <Alert color="yellow" variant="light" title="Waiting on you">
                      {activeSession.waiting_reason}
                    </Alert>
                  ) : null}
                  {activeSession?.last_error ? (
                    <Alert color="red" variant="light" title="Session error">
                      {activeSession.last_error}
                    </Alert>
                  ) : null}

                  {inputBox}
                </Stack>
              </div>
            </>
          )}
      </SidebarLayout>
    </>
  )
}

ProjectChatsIndex.layout = (page: ReactNode) => <AppLayout padding={0}>{page}</AppLayout>

export default ProjectChatsIndex
